import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import LegacySurfaceBridge from "ui/LegacySurfaceBridge";
import SurfaceHost from "ui/SurfaceHost";
import { commentQueryKeys } from "hooks/queries/useCommentsQuery";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { useSurfaceMutationListener } from "hooks/useSurfaceMutationListener";
import type { Comment } from "util/constants";
import type { SurfaceContract } from "types/surfaces";

export interface CommentsDrawerOpenArgs {
  /**
   * Entity type the drawer should show comments for. Typed loosely
   * because callers come from many places (entity action rows,
   * feeds, quiz results) and carry the legacy widened type.
   */
  entityType: Comment["commentable_type"];
  /** Entity id to scope the comments to. */
  entityId: number | string;
  /**
   * Optional initial sort mode. Defaults to `"new"` because that
   * matches the Reddit-style inline view the user already has on
   * desktop.
   */
  initialSort?: "top" | "new" | "old";
  /**
   * Optional kind filter (e.g., `"principle"`). Forwarded to the Next
   * surface's query string.
   */
  kind?: string | null;
  /**
   * Optional callback fired when a mutation (create/update/delete)
   * lands inside the embed. Parent pages usually won't need it
   * because `useSurfaceMutationListener` already invalidates the
   * shared `commentQueryKeys.byCommentable` bucket; the callback is
   * a last-resort hook for surfaces with bespoke caches.
   */
  onMutation?: () => void;
}

interface CommentsDrawerContextValue {
  /** `true` if the `comments_v2_drawer` feature flag is enabled. */
  isAvailable: boolean;
  /** True while the drawer is open. */
  isOpen: boolean;
  /** Programmatically open the drawer for the given entity. */
  open: (args: CommentsDrawerOpenArgs) => void;
  /** Close the drawer. Safe to call if already closed. */
  close: () => void;
}

const CommentsDrawerContext = createContext<CommentsDrawerContextValue | null>(
  null
);

interface CommentsDrawerProviderProps {
  children: React.ReactNode;
}

interface ActiveTarget {
  entityType: Comment["commentable_type"];
  entityId: number | string;
  initialSort: "top" | "new" | "old";
  kind: string | null;
  onMutation?: (() => void) | undefined;
}

/**
 * Single host + bridge instance for the Instagram-style mobile
 * comments drawer. Mounts once near the root of the legacy app
 * (inside `<UserProvider>` so `useUser()` is available) and lets
 * any descendant open the drawer imperatively via
 * `useCommentsDrawer()`.
 *
 * Consolidating the host means:
 *
 *  - Only one iframe loads at a time; closing one card and opening
 *    another reuses the same transport.
 *  - `surface:mutation` events propagated by the embed can always
 *    invalidate the legacy `commentQueryKeys.byCommentable` bucket
 *    for the active entity, even for callers (feed rows, cards)
 *    that don't know anything about TanStack Query.
 *  - The drawer is available from ANY surface (entity profile page,
 *    feed card, grid, notifications) without each caller managing
 *    its own `<SurfaceHost>` lifecycle.
 */
export const CommentsDrawerProvider: React.FC<CommentsDrawerProviderProps> = ({
  children,
}) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isEnabled: drawerEnabled } = useFeatureFlag(
    "comments_v2_drawer",
    true
  );
  const [active, setActive] = useState<ActiveTarget | null>(null);
  const onMutationRef = useRef<(() => void) | null>(null);

  const open = useCallback(
    ({
      entityType,
      entityId,
      initialSort = "new",
      kind = null,
      onMutation,
    }: CommentsDrawerOpenArgs): void => {
      onMutationRef.current = onMutation ?? null;
      setActive({
        entityType,
        entityId,
        initialSort,
        kind,
        ...(onMutation ? { onMutation } : {}),
      });
    },
    []
  );

  const close = useCallback((): void => {
    onMutationRef.current = null;
    setActive(null);
  }, []);

  const handleMutation = useCallback((): void => {
    if (!active) return;
    // Invalidate every legacy comments cache bucket for this entity
    // so count chips + inline tree + deep-link thread all refetch.
    void queryClient.invalidateQueries({
      queryKey: commentQueryKeys.byCommentable(active.entityType, Number(active.entityId)),
      exact: false,
    });
    const fn = onMutationRef.current;
    if (fn) fn();
  }, [active, queryClient]);

  useSurfaceMutationListener({
    entityType: active ? String(active.entityType) : "",
    entityId: active?.entityId ?? null,
    onMutation: handleMutation,
    enabled: Boolean(active) && drawerEnabled,
  });

  const contract = useMemo<SurfaceContract<{
    sort: "top" | "new" | "old";
  }> | null>(() => {
    if (!active) return null;
    return {
      surfaceKind: "comments",
      entityType: String(active.entityType),
      entityId: active.entityId,
      presentation: "drawer",
      initial: { sort: active.initialSort },
      origin: {
        route: `${location.pathname}${location.search}`,
        scrollY: typeof window !== "undefined" ? window.scrollY : 0,
      },
    };
  }, [active, location.pathname, location.search]);

  // Close the drawer when the parent URL changes (navigation back
  // from the comments overlay would be confusing if the drawer
  // stayed mounted on a different page).
  useEffect(() => {
    setActive(null);
  }, [location.pathname]);

  const value = useMemo<CommentsDrawerContextValue>(
    () => ({
      isAvailable: drawerEnabled,
      isOpen: Boolean(active),
      open,
      close,
    }),
    [active, close, drawerEnabled, open]
  );

  return (
    <CommentsDrawerContext.Provider value={value}>
      {children}
      {active && contract ? (
        <SurfaceHost
          open
          onOpenChange={(next) => {
            if (!next) close();
          }}
          presentation="drawer"
          title="Comments"
          testId="comments-drawer-host"
        >
          {({ close: closeHost }) => (
            <LegacySurfaceBridge
              {...contract}
              mode="iframe"
              title="Comments"
              className="h-[80vh] w-full border-0"
              extraQuery={{ sort: active.initialSort }}
              onClose={closeHost}
              onMutation={handleMutation}
            />
          )}
        </SurfaceHost>
      ) : null}
    </CommentsDrawerContext.Provider>
  );
};

export const useCommentsDrawer = (): CommentsDrawerContextValue => {
  const context = useContext(CommentsDrawerContext);
  if (!context) {
    // Soft-fail: returning a no-op keeps the legacy app functional
    // on routes that haven't yet mounted the provider (e.g., auth
    // screens, standalone embeds). Callers gate their behavior on
    // `isAvailable` so this fallback only matters for defense.
    return {
      isAvailable: false,
      isOpen: false,
      open: () => {
        /* no-op */
      },
      close: () => {
        /* no-op */
      },
    };
  }
  return context;
};

export default CommentsDrawerProvider;
